import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { codexApi } from '../api';
import EngagementHeatmap from '../components/EngagementHeatmap';
import ComposeLetterModal from '../components/ComposeLetterModal';
import VoiceRecorder from '../components/VoiceRecorder';
import VoiceNotePlayer from '../components/VoiceNotePlayer';
import { Crown, Flame, Trophy, BookMarked, ChevronLeft, AlertCircle, Send, Calendar, AlertTriangle, Mail, Sword, X, Mic, Coins } from 'lucide-react';

const STATUS_COLOR = {
  thriving: { bg: 'rgba(34,197,94,0.18)', border: 'rgba(34,197,94,0.5)', dot: '#22c55e' },
  slowing:  { bg: 'rgba(234,179,8,0.18)', border: 'rgba(234,179,8,0.5)', dot: '#eab308' },
  at_risk:  { bg: 'rgba(239,68,68,0.18)', border: 'rgba(239,68,68,0.5)', dot: '#ef4444' },
  new:      { bg: 'rgba(168,85,247,0.18)', border: 'rgba(168,85,247,0.5)', dot: '#a855f7' },
};

export default function CodexMentor() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [detail, setDetail] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDefaultId, setComposeDefaultId] = useState(null);
  const [weakness, setWeakness] = useState(null);
  const [missionOpen, setMissionOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);

  const loadDetail = (uid) => Promise.all([
    codexApi.mentorMenteeDetail(uid).catch((e) => { setError(e.message); return null; }),
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/codex/mentor/weakness/${uid}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    }).then((r) => r.ok ? r.json() : null).catch(() => null),
    codexApi.voiceThread(uid).catch(() => []),
  ]).then(([d, w, voice_thread]) => {
    if (d) {
      setDetail({ ...d, voice_thread });
    }
    setWeakness(w);
    setLoading(false);
  });

  useEffect(() => {
    if (userId) {
      loadDetail(userId);
    } else {
      Promise.all([
        codexApi.mentorMentees().catch(() => []),
        codexApi.heatmap(30).catch(() => []),
      ]).then(([roster, hm]) => {
        setList(roster);
        setHeatmap(hm);
      }).catch((e) => setError(e.message)).finally(() => setLoading(false));
    }
  }, [userId]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Summoning command centre…</div>;
  if (error)   return <div style={{ padding: 40, textAlign: 'center', color: 'salmon' }}>{error}</div>;

  if (userId && detail) {
    const p = detail.progress || {};
    return (
      <div data-testid="codex-mentor-detail" style={{ paddingTop: 8 }}>
        <button onClick={() => navigate('/codex/mentor')} className="codex-btn codex-btn-ghost" style={{ marginBottom: 16 }}>
          <ChevronLeft size={14} /> Back to roster
        </button>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="codex-card-gold codex-card" style={{ padding: 26, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="codex-eyebrow">MENTEE PROFILE</div>
            <h1 className="codex-h1" style={{ marginTop: 6, fontSize: '2rem' }}>{detail.user.name}</h1>
            <div style={{ opacity: 0.75, marginTop: 4 }}>{detail.user.email}</div>
          </div>
          <button
            onClick={() => { setComposeDefaultId(detail.user.id); setComposeOpen(true); }}
            className="codex-btn codex-btn-primary"
            data-testid="codex-mentor-detail-send-letter"
          >
            <Send size={14} /> Send Sacred Letter
          </button>
          <button
            onClick={() => setMissionOpen(true)}
            className="codex-btn codex-btn-ghost"
            data-testid="codex-mentor-detail-assign-mission"
            style={{ marginLeft: 8 }}
          >
            <Sword size={14} /> Assign Mission
          </button>
          <button
            onClick={() => setGiftOpen(true)}
            className="codex-btn codex-btn-ghost"
            data-testid="codex-mentor-detail-gift-shekels"
            style={{ marginLeft: 8 }}
          >
            <Coins size={14} /> Gift Shekels
          </button>
          <a
            href="/admin"
            target="_blank"
            rel="noopener noreferrer"
            className="codex-btn codex-btn-ghost"
            data-testid="codex-mentor-schedule-1-1"
            style={{ marginLeft: 8 }}
          >
            <Calendar size={14} /> Schedule 1-1
          </a>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
          {[
            { Icon: Trophy,   label: 'XP',         value: p.xp || 0 },
            { Icon: Crown,    label: 'Level',      value: `Lv ${p.level || 1}` },
            { Icon: Flame,    label: 'Streak',     value: `${p.streak_days || 0}d` },
            { Icon: BookMarked, label: 'Mastered', value: (p.completed_words || []).length },
          ].map((s) => (
            <div key={s.label} className="codex-card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--codex-gold-light)' }}>
                <s.Icon size={14} />
                <span className="codex-eyebrow" style={{ color: 'inherit' }}>{s.label}</span>
              </div>
              <div className="codex-display" style={{ fontSize: 24, marginTop: 4 }}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="codex-card" style={{ padding: 22 }}>
          <div className="codex-eyebrow">RECENT MASTERED WORDS</div>
          {!(p.completed_words || []).length ? (
            <p style={{ opacity: 0.7, marginTop: 8 }}>No words mastered yet — encourage them to open today's scroll.</p>
          ) : (
            <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
              {(p.completed_words || []).slice().reverse().slice(0, 12).map((w) => (
                <div key={w.word_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--codex-line)' }}>
                  <span><span className="codex-display" style={{ color: 'var(--codex-gold-light)' }}>{w.transliteration}</span> · {w.language}</span>
                  <span style={{ opacity: 0.7, fontSize: 12 }}>{new Date(w.completed_at).toLocaleDateString('en-GB', { timeZone: 'Europe/London' })}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shared reflection entries */}
        <div className="codex-card" style={{ padding: 22, marginTop: 16 }} data-testid="codex-mentor-shared-reflections">
          <div className="codex-eyebrow">SHARED REFLECTIONS</div>
          {!(detail.shared_journal_entries || []).length ? (
            <p style={{ opacity: 0.7, marginTop: 8, fontSize: 13 }}>
              No reflections shared with you yet. Mentees decide what to share.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
              {detail.shared_journal_entries.map((j) => (
                <div key={j.id} className="codex-card" style={{ padding: 14, background: 'rgba(0,0,0,0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap', fontSize: 12, opacity: 0.85 }}>
                    <span>{new Date(j.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' })}</span>
                    {j.word_transliteration && <span className="codex-display" style={{ color: 'var(--codex-gold-light)' }}>· {j.word_transliteration}</span>}
                    {j.mood && <span>· {j.mood}</span>}
                  </div>
                  {j.prompt && (
                    <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 6, fontStyle: 'italic' }}>"{j.prompt}"</div>
                  )}
                  <p style={{ lineHeight: 1.7, fontSize: 14, whiteSpace: 'pre-wrap' }}>{j.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Weakness analysis */}
        <div className="codex-card" style={{ padding: 22, marginTop: 16 }} data-testid="codex-mentor-weakness">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} color="#f59e0b" />
            <div className="codex-eyebrow" style={{ color: '#f59e0b' }}>STRUGGLE AREAS · LAST {weakness?.days || 30} DAYS</div>
          </div>
          {!weakness || weakness.total_misses === 0 ? (
            <p style={{ opacity: 0.7, marginTop: 8, fontSize: 13 }}>
              No wrong answers logged in the window. {weakness?.total_misses === 0 ? 'Either thriving or new to the path.' : ''}
            </p>
          ) : (
            <>
              <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
                {weakness.total_misses} miss{weakness.total_misses === 1 ? '' : 'es'} total. Top areas to target with encouragement:
              </div>
              {weakness.top_words?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div className="codex-eyebrow">SCROLLS</div>
                  <ul style={{ marginTop: 6, paddingLeft: 0, listStyle: 'none' }}>
                    {weakness.top_words.map((w) => (
                      <li key={w.word_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--codex-line)', fontSize: 13 }}>
                        <span>
                          <strong style={{ color: 'var(--codex-gold-light)' }}>{w.transliteration}</strong>{' '}
                          <span style={{ opacity: 0.6 }}>· {w.language}</span>
                        </span>
                        <span style={{ opacity: 0.8 }}>
                          {w.miss_count} miss{w.miss_count === 1 ? '' : 'es'}
                          {' '}<span style={{ opacity: 0.55 }}>(L1:{w.by_layer?.[1] || 0} · L2:{w.by_layer?.[2] || 0} · L3:{w.by_layer?.[3] || 0})</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {weakness.top_lessons?.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div className="codex-eyebrow">CHAMBER LESSONS</div>
                  <ul style={{ marginTop: 6, paddingLeft: 0, listStyle: 'none' }}>
                    {weakness.top_lessons.map((l) => (
                      <li key={l.lesson_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--codex-line)', fontSize: 13 }}>
                        <span>
                          <strong style={{ color: 'var(--codex-gold-light)' }}>{l.lesson_title}</strong>{' '}
                          <span style={{ opacity: 0.6 }}>· {l.chamber_slug}</span>
                        </span>
                        <span style={{ opacity: 0.8 }}>{l.miss_count} miss{l.miss_count === 1 ? '' : 'es'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {/* Voice notes send + thread */}
        <div className="codex-card" style={{ padding: 18, marginTop: 16 }} data-testid="codex-mentor-detail-voice">
          <div className="codex-eyebrow" style={{ marginBottom: 8 }}><Mic size={11} style={{ verticalAlign: '-1px' }} /> VOICE NOTES</div>
          <VoiceRecorder
            recipientId={detail.user.id}
            compact
            onSent={() => loadDetail(detail.user.id)}
          />
          {detail.voice_thread && detail.voice_thread.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
              {detail.voice_thread.map((v) => (
                <VoiceNotePlayer
                  key={v.id}
                  note={v}
                  side={v.sender_id === detail.user.id ? 'in' : 'out'}
                />
              ))}
            </div>
          )}
        </div>

        <ComposeLetterModal
          open={composeOpen}
          onClose={() => setComposeOpen(false)}
          mentees={[{ user_id: detail.user.id, name: detail.user.name, email: detail.user.email }]}
          defaultMenteeId={composeDefaultId}
        />

        <AssignMissionModal
          open={missionOpen}
          onClose={() => setMissionOpen(false)}
          menteeId={detail.user.id}
          menteeName={detail.user.name}
        />

        <GiftShekelsModal
          open={giftOpen}
          onClose={() => setGiftOpen(false)}
          menteeId={detail.user.id}
          menteeName={detail.user.name}
        />
      </div>
    );
  }
  // Roster
  const groups = {
    thriving: list.filter((m) => m.status === 'thriving'),
    slowing:  list.filter((m) => m.status === 'slowing'),
    at_risk:  list.filter((m) => m.status === 'at_risk'),
    new:      list.filter((m) => m.status === 'new'),
  };

  return (
    <div data-testid="codex-mentor-roster" style={{ paddingTop: 8 }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="codex-eyebrow">MENTOR COMMAND</div>
          <h1 className="codex-h1" style={{ marginTop: 6 }}>Your Mentees</h1>
          <p style={{ opacity: 0.75, marginTop: 6 }}>
            {list.length} assigned · {groups.thriving.length} thriving · {groups.at_risk.length} need attention
          </p>
        </div>
        <button
          onClick={() => { setComposeDefaultId(null); setComposeOpen(true); }}
          className="codex-btn codex-btn-primary"
          data-testid="codex-mentor-send-letter"
        >
          <Send size={14} /> Send Sacred Letter
        </button>
        <a
          href="/codex/access"
          className="codex-btn codex-btn-ghost"
          data-testid="codex-mentor-access-manager"
          style={{ textDecoration: 'none' }}
        >
          <Crown size={14} /> Manage Access
        </a>
      </motion.div>

      {/* Engagement heatmap */}
      {heatmap && heatmap.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <EngagementHeatmap data={heatmap} />
        </div>
      )}

      {/* Sent letters preview */}
      <SentLettersPanel />

      {groups.at_risk.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="codex-card" style={{ padding: 14, marginBottom: 16, borderColor: 'rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={16} color="#f87171" />
            <strong>{groups.at_risk.length}</strong>{' '}
            <span style={{ opacity: 0.85 }}>{groups.at_risk.length === 1 ? 'mentee has' : 'mentees have'} not opened a scroll in 2+ days. A short note from you can shift the week.</span>
          </div>
        </motion.div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {list.map((m, i) => {
          const c = STATUS_COLOR[m.status] || STATUS_COLOR.new;
          return (
            <motion.button
              key={`${m.user_id}-${i}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.04, 0.4) }}
              whileHover={{ y: -3 }}
              onClick={() => navigate(`/codex/mentor/${m.user_id}`)}
              className="codex-card"
              style={{
                padding: 18, textAlign: 'left', cursor: 'pointer',
                color: 'var(--codex-parchment)',
                borderColor: c.border, background: c.bg,
              }}
              data-testid={`codex-mentor-mentee-${m.user_id}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot }} />
                <span className="codex-eyebrow" style={{ color: c.dot }}>{m.status.replace('_', ' ').toUpperCase()}</span>
              </div>
              <div className="codex-display" style={{ fontSize: 18 }}>{m.name}</div>
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{m.email}</div>
              <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 13, flexWrap: 'wrap' }}>
                <span><Crown size={12} style={{ verticalAlign: -2 }} /> Lv {m.level}</span>
                <span><Flame size={12} style={{ verticalAlign: -2 }} /> {m.streak_days}d</span>
                <span><BookMarked size={12} style={{ verticalAlign: -2 }} /> {m.words_mastered}</span>
              </div>
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 8 }}>
                Last active: {m.last_active_date || 'never'}
              </div>
            </motion.button>
          );
        })}
      </div>

      {list.length === 0 && (
        <div className="codex-card" style={{ padding: 40, textAlign: 'center', marginTop: 16 }}>
          <p>No mentees assigned yet. The admin can assign mentees from the academy Control Center.</p>
        </div>
      )}

      <ComposeLetterModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        mentees={list}
        defaultMenteeId={composeDefaultId}
      />
    </div>
  );
}

/** Preview of letters this mentor has sent. Shown on the roster page. */
function SentLettersPanel() {
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    codexApi.sentLetters()
      .then((arr) => setItems(arr || []))
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;
  const visible = expanded ? items : items.slice(0, 3);

  return (
    <div className="codex-card" style={{ padding: 22, marginBottom: 18 }} data-testid="codex-mentor-sent-letters">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Mail size={16} color="var(--codex-gold-light)" />
        <div className="codex-eyebrow">SACRED LETTERS YOU'VE SENT · {items.length}</div>
      </div>
      <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
        {visible.map((l, i) => (
          <div
            key={`${l.id}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
              borderBottom: '1px solid var(--codex-line)',
              fontSize: 13,
              flexWrap: 'wrap',
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: l.read ? '#22c55e' : 'var(--codex-orange)',
              flexShrink: 0,
            }} title={l.read ? 'Read' : 'Unread'} />
            <span className="codex-display" style={{ color: 'var(--codex-gold-light)' }}>{l.title}</span>
            <span style={{ opacity: 0.7 }}>→ {l.mentee_name || l.mentee_id}</span>
            <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 11 }}>
              {new Date(l.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' })}
              {l.read ? ' · read' : ' · unread'}
            </span>
          </div>
        ))}
      </div>
      {items.length > 3 && (
        <button
          onClick={() => setExpanded((x) => !x)}
          className="codex-btn codex-btn-ghost"
          style={{ marginTop: 12, padding: '6px 10px', fontSize: 11 }}
        >
          {expanded ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}

function AssignMissionModal({ open, onClose, menteeId, menteeName }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [target, setTarget] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !title.trim() || !description.trim()) return;
    setBusy(true); setError('');
    try {
      await codexApi.challengesMentorAssign({
        mentee_id: menteeId,
        title: title.trim(),
        description: description.trim(),
        target: Math.max(1, parseInt(target, 10) || 1),
      });
      setTitle(''); setDescription(''); setTarget(1);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to assign');
    } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      data-testid="codex-mentor-assign-mission-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(11,10,22,0.85)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="codex-card"
        style={{
          maxWidth: 480, width: '100%', padding: 28,
          border: '2px solid rgba(212,175,55,0.4)',
          background: 'linear-gradient(180deg, #1a0e2c, #0b0a16)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div className="codex-eyebrow" style={{ color: 'var(--codex-gold)' }}>ASSIGN MISSION</div>
            <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)', marginTop: 4 }}>For {menteeName}</h2>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fdf6e3', cursor: 'pointer' }} data-testid="mission-modal-close">
            <X size={20} />
          </button>
        </div>

        <label style={{ display: 'block', fontSize: 12, color: 'rgba(245,217,124,0.85)', marginBottom: 6 }}>Title</label>
        <input
          className="codex-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Read aloud Genesis 1"
          maxLength={100}
          data-testid="mission-title"
          style={{ marginBottom: 14 }}
        />

        <label style={{ display: 'block', fontSize: 12, color: 'rgba(245,217,124,0.85)', marginBottom: 6 }}>Description</label>
        <textarea
          className="codex-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          maxLength={400}
          placeholder="Describe what you'd like them to walk through..."
          data-testid="mission-description"
          style={{ marginBottom: 14, resize: 'vertical' }}
        />

        <label style={{ display: 'block', fontSize: 12, color: 'rgba(245,217,124,0.85)', marginBottom: 6 }}>Target (number of times to mark progress)</label>
        <input
          type="number"
          className="codex-input"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          min={1}
          max={30}
          data-testid="mission-target"
          style={{ marginBottom: 16 }}
        />

        {error && (
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(239,68,68,0.18)', color: '#fca5a5', marginBottom: 14, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="codex-btn codex-btn-ghost" disabled={busy}>Cancel</button>
          <button type="submit" className="codex-btn codex-btn-primary" disabled={busy || !title.trim() || !description.trim()} data-testid="mission-modal-submit">
            <Sword size={14} /> Assign
          </button>
        </div>
      </form>
    </div>
  );
}


function GiftShekelsModal({ open, onClose, menteeId, menteeName }) {
  const [amount, setAmount] = useState(50);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const amt = Math.max(1, Math.min(5000, parseInt(amount, 10) || 0));
    if (amt <= 0) { setError('Enter a positive amount.'); return; }
    setBusy(true); setError(''); setSuccess('');
    try {
      const res = await codexApi.treasuryGift(menteeId, amt, note.trim());
      setSuccess(`Blessing sent — ${menteeName} now has ${res.new_balance} Shekels.`);
      setAmount(50);
      setNote('');
      setTimeout(() => { onClose(); setSuccess(''); }, 1400);
    } catch (err) {
      setError(err.message || 'Failed to gift');
    } finally { setBusy(false); }
  };

  const presets = [25, 50, 100, 250];

  return (
    <div
      onClick={onClose}
      data-testid="codex-mentor-gift-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(11,10,22,0.85)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="codex-card"
        style={{
          maxWidth: 460, width: '100%', padding: 28,
          border: '2px solid rgba(212,175,55,0.4)',
          background: 'linear-gradient(180deg, #1a0e2c, #0b0a16)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <div className="codex-eyebrow" style={{ color: 'var(--codex-gold)' }}>SACRED BLESSING</div>
            <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)', marginTop: 4 }}>Gift Shekels to {menteeName}</h2>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fdf6e3', cursor: 'pointer' }} data-testid="gift-modal-close">
            <X size={20} />
          </button>
        </div>

        <p style={{ fontSize: 12, color: 'rgba(253,246,227,0.75)', marginBottom: 18, lineHeight: 1.5 }}>
          A gift sent in love. The mentee will be notified and the Shekels added to their purse instantly.
        </p>

        <label style={{ display: 'block', fontSize: 12, color: 'rgba(245,217,124,0.85)', marginBottom: 6 }}>Amount</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setAmount(p)}
              data-testid={`gift-preset-${p}`}
              className="codex-btn"
              style={{
                padding: '6px 14px', fontSize: 12,
                background: amount === p ? 'rgba(212,175,55,0.18)' : 'rgba(245,217,124,0.04)',
                color: amount === p ? 'var(--codex-gold-light)' : 'rgba(253,246,227,0.7)',
                borderColor: amount === p ? 'rgba(212,175,55,0.5)' : 'rgba(245,217,124,0.15)',
                textTransform: 'none', letterSpacing: '0.05em',
              }}
            >
              <Coins size={12} /> {p}
            </button>
          ))}
        </div>
        <input
          type="number"
          className="codex-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min={1}
          max={5000}
          data-testid="gift-amount"
          style={{ marginBottom: 14 }}
        />

        <label style={{ display: 'block', fontSize: 12, color: 'rgba(245,217,124,0.85)', marginBottom: 6 }}>Note (optional)</label>
        <textarea
          className="codex-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={280}
          placeholder="A blessing for your faithful walk..."
          data-testid="gift-note"
          style={{ marginBottom: 14, resize: 'vertical' }}
        />

        {error && (
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(239,68,68,0.18)', color: '#fca5a5', marginBottom: 14, fontSize: 13 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(212,175,55,0.18)', color: 'var(--codex-gold-light)', marginBottom: 14, fontSize: 13 }} data-testid="gift-success">
            {success}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="codex-btn codex-btn-ghost" disabled={busy}>Cancel</button>
          <button type="submit" className="codex-btn codex-btn-primary" disabled={busy} data-testid="gift-modal-submit">
            <Coins size={14} /> {busy ? 'Sending…' : `Gift ${amount} Shekels`}
          </button>
        </div>
      </form>
    </div>
  );
}
